import React, { useState, useEffect } from "react";
import BaseEmailComponent from "../BaseEmailComponent";
import EmailImageUpload from "../EmailImageUpload";
import "../EmailDetails.css";
import { Form } from "react-bootstrap";
import { validateImage, getImageUrl } from "../../../utils/imageHelper";

const Title_4Img = ({ id, data, onUpdate, styleSettings, title: propsTitle, description: propsDescription, previewMode = false, ...otherProps }) => {
  // Prioritize props (for API streaming) over data prop
  // Note: In preview mode, image1-4 come as direct props via otherProps
  const effectiveData = data || otherProps || {};
  const effectiveTitle = propsTitle || effectiveData?.title || "";
  const effectiveDescription = propsDescription || effectiveData?.description || "";

  // Get images from effectiveData or directly from otherProps (for preview mode)
  const getInitialImage = (key) => {
    return validateImage(effectiveData?.[key]) || validateImage(otherProps?.[key]);
  };

  const [image1, setImage1] = useState(getInitialImage('image1'));
  const [image2, setImage2] = useState(getInitialImage('image2'));
  const [image3, setImage3] = useState(getInitialImage('image3'));
  const [image4, setImage4] = useState(getInitialImage('image4'));

  // Update images when data changes
  useEffect(() => {
    // Check both effectiveData and otherProps for image values
    const img1 = validateImage(effectiveData?.image1) || validateImage(otherProps?.image1);
    const img2 = validateImage(effectiveData?.image2) || validateImage(otherProps?.image2);
    const img3 = validateImage(effectiveData?.image3) || validateImage(otherProps?.image3);
    const img4 = validateImage(effectiveData?.image4) || validateImage(otherProps?.image4);
    
    setImage1(img1);
    setImage2(img2);
    setImage3(img3);
    setImage4(img4);
  }, [effectiveData, otherProps?.image1, otherProps?.image2, otherProps?.image3, otherProps?.image4]);

  // Edit UI with forms and image uploads
  const renderEditUI = ({ title, setTitle, description, setDescription, handleSaveChanges, styleSettings }) => {
    // Function to save all changes at once
    const saveAllChanges = () => {
      handleSaveChanges(title, description, { 
        image1, 
        image2, 
        image3, 
        image4
      });
    };

    // Handle image updates - save immediately when image is uploaded
    const handleImage1Upload = (imageData) => {
      setImage1(imageData);
      // Save immediately with isImageUpload flag
      handleSaveChanges(title, description, { 
        image1: imageData,
        image2, 
        image3, 
        image4
      }, true); // isImageUpload = true
    };

    const handleImage2Upload = (imageData) => {
      setImage2(imageData);
      handleSaveChanges(title, description, { 
        image1,
        image2: imageData, 
        image3, 
        image4
      }, true);
    };

    const handleImage3Upload = (imageData) => {
      setImage3(imageData);
      handleSaveChanges(title, description, { 
        image1,
        image2, 
        image3: imageData, 
        image4
      }, true);
    };

    const handleImage4Upload = (imageData) => {
      setImage4(imageData);
      handleSaveChanges(title, description, { 
        image1,
        image2, 
        image3, 
        image4: imageData
      }, true);
    };

    return (
      <div className="row p-3 rounded" style={{ backgroundColor: 'white' }}>
        <div className="col-12 mb-3">
          <Form.Group className="mb-3">
            <Form.Label><strong>Title</strong></Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onBlur={saveAllChanges}
            />
          </Form.Group>
        </div>

        <div className="col-md-6 mb-3">
          <EmailImageUpload
            initialImage={image1}
            onImageUpload={handleImage1Upload}
            allowRegenerate={true}
            label="Image 1"
          />
        </div>

        <div className="col-md-6 mb-3">
          <EmailImageUpload
            initialImage={image2}
            onImageUpload={handleImage2Upload}
            allowRegenerate={true}
            label="Image 2"
          />
        </div>

        <div className="col-md-6 mb-3">
          <EmailImageUpload
            initialImage={image3}
            onImageUpload={handleImage3Upload}
            allowRegenerate={true}
            label="Image 3"
          />
        </div>

        <div className="col-md-6 mb-3">
          <EmailImageUpload
            initialImage={image4}
            onImageUpload={handleImage4Upload}
            allowRegenerate={true}
            label="Image 4"
          />
        </div>
      </div>
    );
  };

  // View UI for display mode - 2x2 grid using table for email compatibility
  const renderViewUI = ({ title, description, styleSettings, data }) => {
    const { primaryColor, secondaryColor, fontFamily } = styleSettings || {};
    
    // Get images from data if available (for API streaming)
    const displayImage1 = image1 || data?.image1 || null;
    const displayImage2 = image2 || data?.image2 || null;
    const displayImage3 = image3 || data?.image3 || null;
    const displayImage4 = image4 || data?.image4 || null;

    // Get safe URLs for all images
    const imageUrl1 = getImageUrl(displayImage1);
    const imageUrl2 = getImageUrl(displayImage2);
    const imageUrl3 = getImageUrl(displayImage3);
    const imageUrl4 = getImageUrl(displayImage4);

    // Common image cell style - no gaps
    const imageCellStyle = {
      width: "50%",
      padding: "0",
      margin: "0",
      textAlign: "center",
      verticalAlign: "top",
      lineHeight: "0",
      fontSize: "0"
    };

    // Common image style - square images, no gaps
    const imageStyle = {
      width: "100%",
      height: "150px",
      objectFit: "contain",
      display: "block",
      verticalAlign: "bottom",
      border: "0"
    };

    // Placeholder style - square
    const placeholderStyle = {
      height: "150px",
      width: "100%",
      backgroundColor: "#f5f5f5",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
    };

    return (
      <table 
        width="100%" 
        cellPadding="0" 
        cellSpacing="0" 
        border="0"
        style={{ fontFamily, borderCollapse: "collapse", borderSpacing: "0" }}
      >
        {/* Title Row */}
        {title && (
          <tr>
            <td colSpan="2" style={{ padding: "20px 20px 10px 20px", textAlign: "center" }}>
              <h2 style={{ 
                fontSize: "28px", 
                marginBottom: "12px",
                margin: "0",
                color: secondaryColor || "#a1894c",
                fontFamily,
                fontWeight: "bold"
              }}>
                {title}
              </h2>
            </td>
          </tr>
        )}
        
        {/* Container row to limit width */}
        <tr>
          <td colSpan="2" align="center">
            <table 
              width="400" 
              cellPadding="0" 
              cellSpacing="0" 
              border="0"
              style={{ borderCollapse: "collapse", borderSpacing: "0", margin: "0 auto" }}
            >

        {/* Top Row - Image 1 and Image 2 */}
        <tr>
          <td style={imageCellStyle}>
            {imageUrl1 ? (
              <img 
                src={imageUrl1} 
                alt="Feature 1" 
                style={imageStyle} 
              />
            ) : (
              <div style={placeholderStyle}>
                <img
                  src="/img-preview.svg"
                  alt="Feature 1 placeholder"
                  style={{ maxHeight: "80px" }}
                />
              </div>
            )}
          </td>
          <td style={imageCellStyle}>
            {imageUrl2 ? (
              <img 
                src={imageUrl2} 
                alt="Feature 2" 
                style={imageStyle} 
              />
            ) : (
              <div style={placeholderStyle}>
                <img
                  src="/img-preview.svg"
                  alt="Feature 2 placeholder"
                  style={{ maxHeight: "80px" }}
                />
              </div>
            )}
          </td>
        </tr>

        {/* Bottom Row - Image 3 and Image 4 */}
        <tr>
          <td style={imageCellStyle}>
            {imageUrl3 ? (
              <img 
                src={imageUrl3} 
                alt="Feature 3" 
                style={imageStyle} 
              />
            ) : (
              <div style={placeholderStyle}>
                <img
                  src="/img-preview.svg"
                  alt="Feature 3 placeholder"
                  style={{ maxHeight: "80px" }}
                />
              </div>
            )}
          </td>
          <td style={imageCellStyle}>
            {imageUrl4 ? (
              <img 
                src={imageUrl4} 
                alt="Feature 4" 
                style={imageStyle} 
              />
            ) : (
              <div style={placeholderStyle}>
                <img
                  src="/img-preview.svg"
                  alt="Feature 4 placeholder"
                  style={{ maxHeight: "80px" }}
                />
              </div>
            )}
          </td>
        </tr>
            </table>
          </td>
        </tr>
      </table>
    );
  };

  // Merge effective data with title/description from props
  const mergedData = {
    ...effectiveData,
    title: effectiveTitle,
    description: effectiveDescription,
  };

  return (
    <BaseEmailComponent
      id={id}
      data={mergedData}
      onUpdate={onUpdate}
      componentName="Title with 4 Images"
      styleSettings={styleSettings}
      renderEditUI={renderEditUI}
      renderViewUI={renderViewUI}      previewMode={previewMode}
    />
  );
};

export default Title_4Img;

